import { useEffect, useMemo, useRef, useState } from 'react'
import { isSupported, play, stop } from '@/audio/player'
import { signals } from '@/core/data'
import type { Key } from '@/core/i18n'
import type { SignalData } from '@/core/schema'
import { durationS, timeline } from '@/core/signals'
import { resolveCite, ruleCitation, ruleText } from '@/core/sources'
import { WHISTLES, whistle, type Whistle } from '@/core/whistle'
import { BereichWahl } from './BereichWahl'
import { BinnenSignale } from './BinnenSignale'
import { useSettings } from './settings'

const GROUPS: SignalData['context'][] = [
  'manoeuvre',
  'warning',
  'narrow-channel',
  'restricted-visibility',
  'identity',
  'distress',
]

function groupKey(context: SignalData['context'], part: 'label' | 'hint'): Key {
  return (part === 'label' ? `signals.group.${context}` : `signals.group.${context}.hint`) as Key
}

function stepKey(kind: string): Key {
  return `signals.step.${kind}` as Key
}

function voiceKey(id: Whistle['id']): Key {
  return `signals.voice.${id}` as Key
}

/**
 * Schallsignale: hören, mitzählen, nachlesen.
 *
 * Der Ton entsteht im Browser aus den Daten, nicht aus einer Audiodatei. Die Zeitleiste
 * unter dem Signal zeigt, was zu hören ist, und die Stimme lässt sich umstellen, weil die
 * Tonhöhe nach Anhang III von der Schiffsgröße abhängt.
 */
export function Signals() {
  const { settings } = useSettings()
  /**
   * Welche Ordnung gilt, entscheidet der Bereich, wie überall in der App. Vorher zeigte
   * dieser Abschnitt im Binnenbereich die Seesignale, und das ist an genau der Stelle
   * falsch, an der es weh tut: ein kurzer Ton heißt auf dem Rhein etwas anderes.
   */
  return (
    <>
      <BereichWahl mitHinweis />
      {settings.bereich === 'binnen' ? <BinnenSignale /> : <SeeSignale />}
    </>
  )
}

/** Die Seesignale aus Teil D der Regeln. */
function SeeSignale() {
  const { t, pick } = useSettings()
  const [signalId, setSignalId] = useState(signals[0]?.id ?? '')
  const [voiceId, setVoiceId] = useState<Whistle['id']>('mittel')
  const [playingId, setPlayingId] = useState<string | undefined>()
  const mounted = useRef(true)

  const signal = useMemo(
    () => signals.find((s) => s.id === signalId) ?? signals[0]!,
    [signalId],
  )
  const steps = useMemo(() => timeline(signal), [signal])
  const total = durationS(signal)
  const voice = whistle(voiceId)

  function repeatNote(item: SignalData): string {
    if (item.repeatS <= 0) return t('signals.repeat.once')
    const minutes = item.repeatS / 60
    return minutes === 1
      ? t('signals.repeat.minute')
      : t('signals.repeat.minutes', { minutes })
  }

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      stop()
    }
  }, [])

  async function onPlay(target: SignalData): Promise<void> {
    if (playingId === target.id) {
      stop()
      setPlayingId(undefined)
      return
    }
    setPlayingId(target.id)
    try {
      await play(target, whistle(voiceId))
    } finally {
      if (mounted.current) setPlayingId((id) => (id === target.id ? undefined : id))
    }
  }

  return (
    <section className="signals" data-testid="signals">
      {GROUPS.map((group) => {
        const items = signals.filter((s) => s.context === group)
        if (items.length === 0) return null
        return (
          <div className="catalogue__group" key={group} data-group={group}>
            <p className="catalogue__rule">
              {t(groupKey(group, 'label'))} <span>{t(groupKey(group, 'hint'))}</span>
            </p>
            <div className="catalogue__items">
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`states__item${item.id === signal.id ? ' states__item--on' : ''}`}
                  data-signal={item.id}
                  onClick={() => setSignalId(item.id)}
                >
                  <span className="states__rule">{pick(item.name)}</span>
                  <span>{pick(item.meaning)}</span>
                </button>
              ))}
            </div>
          </div>
        )
      })}

      <article className="detail" data-testid="signal-detail">
        <h2>{pick(signal.name)}</h2>
        <p className="detail__text">{pick(signal.meaning)}</p>

        <div className="signal__bar" data-testid="signal-timeline" data-signal={signal.id}>
          {steps.map((step, index) => (
            <span
              key={`${step.kind}-${index}`}
              className={`signal__step signal__step--${step.kind}`}
              data-kind={step.kind}
              style={{ flexGrow: step.duration }}
              title={`${t(stepKey(step.kind))}, ${step.duration.toFixed(1)} s`}
            />
          ))}
        </div>
        <p className="signal__legend" data-testid="signal-length">
          {t('signals.length', {
            steps: steps
              .filter((step) => step.kind !== 'silence')
              .map((step) => t(stepKey(step.kind)))
              .join(' · '),
            seconds: total.toFixed(0),
          })}
        </p>

        <div className="signal__controls">
          <button
            type="button"
            className="signal__play"
            data-testid="signal-play"
            disabled={!isSupported()}
            onClick={() => void onPlay(signal)}
          >
            {playingId === signal.id ? t('signals.stop') : t('signals.play')}
          </button>
          <span className="signal__voices" data-testid="signal-voices">
            {WHISTLES.map((v) => (
              <button
                key={v.id}
                type="button"
                className={`modes__item${voiceId === v.id ? ' modes__item--on' : ''}`}
                data-voice={v.id}
                onClick={() => setVoiceId(v.id)}
                title={t('signals.voice.title', { low: v.band[0], high: v.band[1] })}
              >
                {t(voiceKey(v.id))}
              </button>
            ))}
          </span>
        </div>
        <p className="signal__legend" data-testid="signal-voice-note">
          {t('signals.voice.note', {
            hz: voice.hz,
            name: t(voiceKey(voice.id)),
            low: voice.band[0],
            high: voice.band[1],
          })}
        </p>

        <p className="detail__text">{pick(signal.explanation)}</p>
        <p className="detail__text" data-testid="signal-repeat">
          {repeatNote(signal)}
        </p>

        <h3 className="detail__legend" data-testid="signal-citation">
          {ruleCitation(signal.rule, signal.cite)}
        </h3>
        <ul className="detail__quotes">
          {signal.cite.map((entry) => {
            const found = resolveCite(signal.rule, entry, signal.scope)
            return (
              <li key={entry}>
                {found.rule !== signal.rule && <strong>{`Rule ${found.rule}: `}</strong>}
                {ruleText(found.paragraph, signal.scope === 'inland' ? 'inland' : 'international')}
              </li>
            )
          })}
        </ul>
        <p className="detail__disclaimer">{t('signals.disclaimer')}</p>
      </article>
    </section>
  )
}
